/**
 * PURPOSE: El GRAFO DE FASES visto desde la interfaz (FTR-0028 WG5, D-27/D-28/D-29, §24–§25).
 * RESPONSIBILITIES: Enseñar, lado por lado, de dónde sale cada participante de la fase final y en
 *   qué estado está — resuelto, pendiente, bloqueado, en conflicto o desactualizado — y ofrecer las
 *   tres únicas acciones que el administrador necesita: recalcular, bloquear un lado y forzar un
 *   reemplazo.
 *
 *   EL CLIENTE NO ELIGE PARTICIPANTES. Ni aquí ni en ningún otro sitio del Centro. "Actualizar
 *   clasificados" pide al servidor que recorra el grafo; quién entra en cada lado lo decide el
 *   resultado del partido de origen. Lo único que se envía desde aquí es una AUTORIZACIÓN: "sí, sé
 *   que esta semifinal ya se jugó y aun así quiero que reciba al ganador corregido".
 *
 *   "FORZAR" ES LADO A LADO, NUNCA GLOBAL (D-29). Un botón de "forzar todo" convertiría la
 *   protección de los partidos jugados en una casilla que se marca sin mirar. Aquí cada conflicto
 *   tiene su propio botón, con su propio texto, y forzar uno no toca los demás. Y forzar NO borra el
 *   resultado del partido: cambia el participante y deja el acta como estaba, para que sea una
 *   persona quien decida qué hacer con ella.
 *
 *   UN LADO BLOQUEADO ES UNA DECISIÓN, NO UN ERROR. `locked` significa "este lado lo pongo yo";
 *   la recomputación lo salta y lo dice. Por eso se muestra con el mismo peso visual que un lado
 *   resuelto y se puede desbloquear desde el mismo sitio.
 * COLLABORATORS: ProgrammingCenterProvider.jsx, hooks/useProgrammingCenterData.js,
 *   services/programmingCenterService.js, utils/programmingCenterGraph.js.
 * ROUTE: winscore/src/area/FasesArea/ProgrammingCenter/panels/FinalsPanel.jsx
 */
import React, { useMemo, useState } from "react";
import { FiAlertTriangle, FiLock, FiRefreshCw, FiUnlock, FiZap } from "react-icons/fi";
import { toast } from "react-hot-toast";

import { useProgrammingCenter } from "../ProgrammingCenterProvider.jsx";
import {
  SIDE_STATUS_META,
  describeRecompute,
  graphNodeTitle,
  sideParticipantLabel,
} from "../../../../utils/programmingCenterGraph.js";
import SidePanel from "../components/SidePanel.jsx";

const FinalsPanel = ({ open, onClose }) => {
  const { graph, graphQuery, actions, busy, index } = useProgrammingCenter();
  const [lastRun, setLastRun] = useState(null);

  const nodes = useMemo(() => graph?.nodes || [], [graph]);
  const counts = graph?.counts || { nodes: 0, edges: 0, locked: 0, pending: 0, conflicts: 0 };

  const runRecompute = async (options = {}) => {
    const result = await actions.recomputeGraph(options);
    if (result.ok) {
      setLastRun(result.data);
      toast.success(describeRecompute(result.data));
    }
    return result;
  };

  const toggleLock = async (node, side) => {
    const result = await actions.setGraphSource({
      idmatch: node.idmatch,
      side: side.side,
      locked: !side.locked,
    });
    if (result.ok) {
      toast.success(side.locked ? "Lado desbloqueado." : "Lado bloqueado: no se recalculará.");
    }
  };

  const forceSide = async (node, side) => {
    const confirmed = window.confirm(
      `${graphNodeTitle(node)} · lado ${side.side} ya tiene actividad competitiva.\n\n` +
        "Forzar el reemplazo cambia el participante y DEJA el resultado tal como está. " +
        "Queda registrado en la auditoría. ¿Continuar?"
    );
    if (!confirmed) return;
    await runRecompute({ reason: "force", force: [{ idmatch: node.idmatch, side: side.side }] });
  };

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title="Fase final y clasificados"
      subtitle={
        counts.nodes
          ? `${counts.nodes} partidos en el grafo · ${counts.pending} lados por definir · ${counts.conflicts} en conflicto`
          : "Todavía no hay fase final materializada"
      }
    >
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => runRecompute({ reason: "manual" })}
          disabled={Boolean(busy)}
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-black text-white transition hover:bg-blue-700 disabled:opacity-50"
        >
          <FiRefreshCw className="text-sm" />
          Actualizar clasificados
        </button>
        <button
          type="button"
          onClick={() => {
            if (
              window.confirm(
                "Resolver las posiciones de grupo aunque el grupo todavía no esté terminado. " +
                  "La tabla puede cambiar con los partidos que faltan. ¿Continuar?"
              )
            ) {
              runRecompute({ reason: "manual", forcePositions: true });
            }
          }}
          disabled={Boolean(busy)}
          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-black text-slate-600 transition hover:border-amber-300 hover:text-amber-700 disabled:opacity-50"
        >
          Adelantar posiciones de grupo
        </button>
      </div>

      {lastRun ? (
        <p className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs font-bold text-slate-600">
          {describeRecompute(lastRun)}
        </p>
      ) : null}

      {graph?.cycles?.length ? (
        <p className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs font-black text-rose-800">
          El grafo tiene {graph.cycles.length} ciclo(s): esos partidos dependen unos de otros y no se
          pueden ordenar. Revisa sus referencias; el resto del cuadro se recalcula igual.
        </p>
      ) : null}

      {lastRun?.staleWinners?.length ? (
        <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-bold text-amber-800">
          {lastRun.staleWinners.length} partidos tienen un ganador guardado que no coincide con su
          marcador. El Centro no reescribe actas: manda el marcador para clasificar, y la diferencia
          se revisa en Resultados.
        </p>
      ) : null}

      <section className="mt-4 space-y-2">
        {graphQuery?.isLoading ? (
          <p className="text-xs font-bold text-slate-500">Cargando el grafo…</p>
        ) : null}

        {!graphQuery?.isLoading && !nodes.length ? (
          <p className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs font-bold text-slate-500">
            Ningún partido depende todavía de otro. La fase final aparece aquí en cuanto se
            materializa, y los cruces se rellenan solos a medida que se oficializan los partidos.
          </p>
        ) : null}

        {nodes.map((node) => (
          <article
            key={node.idmatch}
            className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
          >
            <header className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-black text-slate-900">{graphNodeTitle(node)}</p>
              <div className="flex items-center gap-1.5">
                {node.adopted ? (
                  <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-violet-700">
                    Adoptado
                  </span>
                ) : null}
                {node.hasActivity ? (
                  <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-black uppercase tracking-wide text-slate-600">
                    Con actividad
                  </span>
                ) : null}
              </div>
            </header>

            <ul className="mt-2 space-y-1.5">
              {node.sides.map((side) => {
                const meta = SIDE_STATUS_META[side.status] || SIDE_STATUS_META.pending;
                return (
                  <li
                    key={side.side}
                    className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border px-2.5 py-2 ${meta.className}`}
                  >
                    <div className="min-w-0">
                      <p className="truncate text-[11px] font-black uppercase tracking-wide text-slate-500">
                        {side.label || `Lado ${side.side}`}
                      </p>
                      <p className="truncate text-xs font-black text-slate-800">
                        {sideParticipantLabel(side, index)}
                      </p>
                      <p className="text-[11px] font-bold text-slate-500">{meta.describe(side)}</p>
                    </div>

                    <div className="flex shrink-0 items-center gap-1.5">
                      {side.status === "conflict" ? (
                        <button
                          type="button"
                          onClick={() => forceSide(node, side)}
                          disabled={Boolean(busy)}
                          className="flex items-center gap-1 rounded-lg border border-rose-300 bg-white px-2 py-1 text-[11px] font-black text-rose-700 transition hover:bg-rose-50 disabled:opacity-50"
                        >
                          <FiZap className="text-xs" />
                          Forzar reemplazo
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => toggleLock(node, side)}
                        disabled={Boolean(busy)}
                        aria-label={side.locked ? "Desbloquear lado" : "Bloquear lado"}
                        title={
                          side.locked
                            ? "Desbloquear: el motor volverá a calcular este lado"
                            : "Bloquear: el motor dejará de calcular este lado"
                        }
                        className="rounded-lg border border-slate-200 bg-white p-1.5 text-slate-500 transition hover:border-blue-300 hover:text-blue-700 disabled:opacity-50"
                      >
                        {side.locked ? (
                          <FiUnlock className="text-xs" />
                        ) : (
                          <FiLock className="text-xs" />
                        )}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </article>
        ))}
      </section>

      {counts.conflicts ? (
        <p className="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs font-bold text-amber-800">
          <FiAlertTriangle className="mt-0.5 shrink-0 text-sm" />
          Un conflicto significa que el partido de destino ya tiene actividad competitiva y el
          resultado del origen pide otro participante. El Centro no lo cambia por su cuenta: decides
          tú, lado a lado, y el resultado del partido nunca se borra.
        </p>
      ) : null}
    </SidePanel>
  );
};

export default FinalsPanel;
